<template>
	<div style="overflow-x: auto;width: 100%;margin-top: 5px;">

		<p v-if="!hideRecordCount" class="float-right record-count">
			<template v-if="!totalPaginatedRecords">
				{{ recordCount }} {{recordName}}s found.
			</template>
			<template v-else>
				{{ totalPaginatedRecords }} {{recordName}}s found.
			</template>
		</p>

		<div v-if="hasSlot" class="top-table-container">
			<slot></slot>
		</div>

		<table class="table table-sm table-list">
			<thead v-if="!noHeader">
				<tr>
					<th v-if="hasDragHandle">
						<i class="fa fa-sort"></i>
					</th>
					<th v-if="recordsSelectable">

						<label class="record-select-label">
							<input type="checkbox" v-on:change="selectDeselectAll" v-model="bulkSelect">
						</label>

					</th>
					<th v-for="(field,index) in mutatedFields" 
						:style="{textAlign: field.align}" 
						:class="{sortable: sortable}"
						:title="sortable ? 'Click to sort' : field.label"
						v-on:click="headerClick(index)">
						{{field.label}}
						<i v-if="sortable && columnSortedIndex == index && sorted" class="fa" :class="{'fa-caret-up': sortedAsc, 'fa-caret-down': !sortedAsc}"></i>
					</th>
					<th v-if="hasEdit || hasDelete || otherButtons"></th>
				</tr>
			</thead>
			<component :is="rowsDraggable ? 'draggable' : 'tbody'" element="tbody" :handle="hasDragHandle ? '.fa-sort' : false" v-model="localRecords" v-on:end="dragEnd">
				<template v-if="typeof loading != 'undefined' && loading">
					<!-- SKELETON LOADER -->
					<tr v-for="n in 5">
						<td v-if="recordsSelectable"><div class="skeleton-cell"></div></td>
						<td v-for="field in mutatedFields"><div class="skeleton-cell"></div></td>
					</tr>
				</template>
				<template v-else>
					<tr :title="'rowTitle' in record ? record.rowTitle : ''" v-for="(record,index) in mutatedRecords" v-if="!record.tfoot" :class="[{'clickable-row' : clickableRow}, record.rowClasses]">
						<td v-if="hasDragHandle">
							<i class="fa fa-sort"></i>
						</td>

						<td v-if="recordsSelectable">
							<label class="record-select-label" v-on:click="toggleRecordSelect(record)">
								<input type="checkbox" :value="record.fieldId" v-model="selectedRecords" v-on:change='emitSelectedRecords'>
							</label>
						</td>

						<td v-for="field in mutatedFields" 
							v-on:click="rowClick(record)"
							:class="{
								'text-right': 'align' in field && field.align == 'right', 'text-center': 'align' in field && field.align == 'center'
							}">

							<i v-if="field.type == 'boolean' && record[field.key] == 1" class="fa fa-check"></i>

							<i v-else-if="field.type == 'boolean' && record[field.key] == 0" class="fa fa-times"></i>

							<router-link v-else-if="field.type == 'link'" :to="'link' in record[field.key] ? record[field.key].link : ''" v-html="record[field.key].text">
								
							</router-link>

							<a v-else-if="field.type == 'external-link'" :href="record[field.key].link" v-html="record[field.key].text" target="_blank"></a>

							<span v-else v-html="record[field.key]"></span>

						</td>
						<td class="text-right" v-if="hasEdit || hasDelete || otherButtons">

							<template v-if="collapseButtons">

								<button v-on:click="toggledRowButtonIndex == index ? toggledRowButtonIndex = null : toggledRowButtonIndex = index" class="btn btn-link btn-sm button-toggle-button" v-if="mutateButtons(record).length > 0 || hasEdit || hasDelete" type="button">
									<i class="fa" :class="{'fa-times' : toggledRowButtonIndex == index, 'fa-ellipsis-v' : toggledRowButtonIndex != index}"></i>
								</button>

								<div class="table-row-buttons" :class="{
									'hidden-buttons' : toggledRowButtonIndex != index && (mutateButtons(record).length > 0 || hasEdit || hasDelete)
								}">

									<template v-if="!record[recordLockedProperty] || lockedRecordsEditable">
										<button v-if="hasEdit === true" class="btn btn-sm" :class="editButtonClass ? editButtonClass : 'btn-light '"  v-on:click="editRecord(record,index)">
											<i class="fa fa-pencil-alt"></i> {{computedEditButtonText}}
										</button>

										<template v-else-if="hasEdit">
											<a v-if="editNewTab" :href="record.editUrl" :class="editButtonClass ? editButtonClass : 'btn-light '" class="btn btn-sm" target="_blank">
												<i v-if="computedEditButtonIcon" class="fa" :class="computedEditButtonIcon"></i> {{computedEditButtonText}}
											</a>
											<router-link v-else 
												:to="record.editUrl" 
												class="btn btn-sm" 
												:class="editButtonClass ? editButtonClass : 'btn-light '">
												<i class="fa fa-pencil-alt"></i> {{computedEditButtonText}}
											</router-link>
										</template>
									</template>

									<template v-for="button in mutateButtons(record)">
										<a v-if="'link' in button && 'newTab' in button && button.newTab" :href="button.link" target="_blank" :class="button.class" :title="'title' in button ? button.title : button.text" class="btn btn-sm other-button">
											<i v-if="button.icon" class="fa" :class="button.icon"></i>
											{{ button.text }}
										</a>
										<router-link v-else-if="'link' in button" class="btn btn-sm other-button" :to="button.link" :class="button.class" :title="'title' in button ? button.title : button.text">
											<i v-if="button.icon" class="fa" :class="button.icon"></i>
											{{ button.text }}
										</router-link>
										<button v-else-if="'click' in button" type="button" class="btn btn-sm other-button" :class="button.class" :title="'title' in button ? button.title : button.text" v-on:click="otherButtonClick(record,button.click,index)">
											<i v-if="button.icon" class="fa" :class="button.icon"></i>
											{{ button.text }}
										</button>
									</template>

									<button v-if="hasDelete && !record[recordLockedProperty]" class="btn btn-outline-danger btn-sm" type="button" style="border: 1px solid var(--danger)" v-on:click="deleteRecord(record, index)">
										<i class="fa fa-trash"></i>
									</button>

								</div>
							</template>

							<template v-else>
								<template v-if="!record[recordLockedProperty] || lockedRecordsEditable">
									<button v-if="hasEdit === true" type="button" class="btn btn-sm edit-button" :class="editButtonClass ? editButtonClass : 'btn-light '" v-on:click="editRecord(record,index)">
										<i v-if="computedEditButtonIcon" class="fa" :class="computedEditButtonIcon"></i> {{computedEditButtonText}}
									</button>

									<template v-else-if="hasEdit">
										<a v-if="editNewTab" :href="record.editUrl" :class="editButtonClass ? editButtonClass : 'btn-light '" class="btn btn-sm edit-button" target="_blank">
											<i v-if="computedEditButtonIcon" class="fa" :class="computedEditButtonIcon"></i> {{computedEditButtonText}}
										</a>
										<router-link v-else-if="record.editUrl" 
											:to="record.editUrl" 
											:class="editButtonClass ? editButtonClass : 'btn-light '" 
											class="btn btn-sm edit-button">
											<i v-if="computedEditButtonIcon" class="fa" :class="computedEditButtonIcon"></i> {{computedEditButtonText}}
										</router-link>
									</template>
								</template>

								<template v-for="button in mutateButtons(record)"> 
									<a v-if="'link' in button && 'newTab' in button && button.newTab" :href="button.link" target="_blank" :class="button.class" :title="'title' in button ? button.title : button.text" class="btn btn-sm other-button">
											<i v-if="button.icon" class="fa" :class="button.icon"></i>
											{{ button.text }}
										</a>
									<router-link v-else-if="'link' in button" class="btn btn-sm other-button" :to="button.link" :class="button.class" :title="'title' in button ? button.title : button.text">
										<i v-if="button.icon" class="fa" :class="button.icon"></i>
										{{ button.text }}
									</router-link>
									<button v-else-if="'click' in button" type="button" class="btn btn-sm other-button" :class="button.class" v-on:click="otherButtonClick(record,button.click,index)" :title="'title' in button ? button.title : button.text">
										<i v-if="button.icon" class="fa" :class="button.icon"></i>
										{{ button.text }}
									</button>
								</template>

								<button v-if="hasDelete && !record[recordLockedProperty]" type="button" class="btn btn-outline-danger btn-sm" v-on:click="deleteRecord(record,index)">
									<i class="fa fa-trash"></i>
								</button>
							</template>
							
						</td>
					</tr>
					<tr v-if="!records || records.length == 0">
						<td :colspan="mutatedFields.length">No {{localRecordName}}s</td>
						<td v-if="hasEdit || hasDelete"></td>
					</tr>
				</template>
			</component>
			<tfoot v-if="!loading">
				<tr v-for="record in mutatedRecords" 
					v-if="record.tfoot"
					:class="[{'clickable-row' : clickableRow}, record.rowClasses]">
					
					<td v-for="field in mutatedFields" :class="{'text-right': 'align' in field && field.align == 'right', 'text-center': 'align' in field && field.align == 'center'}">
						<i v-if="field.type == 'boolean' && record[field.key] == 1" class="fa fa-check"></i>
						<i v-else-if="field.type == 'boolean' && record[field.key] == 0" class="fa fa-times"></i>
						<span v-else v-html="record[field.key]"></span>
					</td>
					<td v-if="hasEdit || hasDelete || otherButtons"></td>
				</tr>
			</tfoot>
		</table>

		<modal-dialog
			:title="confirmDialog.title" 
			:modal-visible="confirmDialog.visible" 
			:confirm-button-text="confirmDialog.buttonText"
			:button-class="confirmDialog.buttonClass"
			v-on:confirm="executeDelete(confirmDialog.parameters)"
			v-on:closeModal="confirmDialog.visible = false">
			{{ confirmDialog.message }}
		</modal-dialog>
	</div>
</template>

<script type="text/javascript">
	import ModalDialog from '../components/ModalDialog'
	import draggable from 'vuedraggable'

	export default {
		name: "TableList",
		components: {
			ModalDialog,
			draggable
		},
		props: [
			"records", /*
				array of objects whose properties should match fields. Additional meta properties include: 
					rowClasses: (string), to be applied to tr element
					tfoot: (boolean), places row in table footer

			*/
			"fields",
			"mobileBreakpoint", //if not set default is 1024
			"idField",

			//hasEdit
			//if true, its a button that resolves to "edit" event on click, otherwise its a link that contains the root path "hasEdit/{record_id}" (slash is included in hasEdit)
			//can accept {?} anywhere in the url as a placeholder for the record id (ex. "patient/{id}/reports")
			"hasEdit",
			"editNewTab", //if true, edit clicks open link in new tab
			"recordLockedProperty", //if defined property is true on the record, edit/delete will not show up for that record
			"lockedRecordsEditable", //if true, locked records only take away the delete button
			"hasDelete", //true/false
			"simpleDelete", //if true, skip the confirm modal and ajax call and simply emit delete event
			"otherButtons", /* [{
									link :string,
									click: string,
									condition: [fieldKeyToCompare, value], //only show button if record[fieldKeyToCompare] == value
									text: string,
									title: string,
									class: string,
									icon: 'fa-string',
									newTab: boolean
								}]

			*/
			"recordName",
			"deleteEndpoint", //api url endpoint for delete request //PurchaseOrder/delete
			"clickableRow",
			"recordsSelectable", //add a checkbox to select a subset of records
			"recordsSelected", //array of records selected
			//"selectedRecordsProp", //sets the value of selected subset
			"noHeader", //if true, header is not shown,
			"editButtonText", //change the name of edit button, other default is "Edit"
			"editButtonIcon", //change the icon of the edit button, otherwise default is fa-pencil-alt
			"editButtonClass",
			"buttonsShowHover", //NOT IMPLEMENTED YET only show buttons on hover
			"collapseButtons", //if true, buttons will collapse into "Actions" dropdown
			"totalPaginatedRecords", //if set, this will show as record count instead of actual counted records passed in
			"hideRecordCount", //if false, shows a statement for number of records found above table
			"sortable", //if true, headers can be clicked to sort up or down
			"rowsDraggable", //if true, rows can be re-ordered by user. On v-on:end (drag end), the dragEnd event is emitted with the event as the payload (event includes oldIndex and newIndex properties)
			"hasDragHandle", //if true, drag handle column will be visible with icons
			"loading" //if true, loader skeleton will show instead of records
		],
		data() {
			return {
				confirmDialog: {
					visible: false,
					title: "",
					buttonText: "Delete",
					buttonClass: "btn-outline-danger",
					parameters: "",
					successFunction: "",
					record: {},
					index: null
				},
				columnSortedIndex: false,
				sortedAsc: true,
				toggledRowButtonIndex: null,
				sorted: false,
				windowWidth: window.innerWidth,
				selectedRecords: [],
				bulkSelect: false,
				localRecords: []
			}
		},
		watch: {
			recordsSelected() {
				let vm = this
				if (this.recordsSelected) {
					this.selectedRecords = this.recordsSelected.map(record => {
						if (vm.idField)
							return record[vm.idField]
						else
							return record.id
					})
					
					if (this.selectedRecords.length > 0)
						this.bulkSelect = true
					else
						this.bulkSelect = false
				}
			}
		},
		computed: {
			hasSlot() {
				if ("default" in this.$slots) 
					return true
				else 
					return false
			},
			//count records (exclude tfoot)
			recordCount() {
				let cnt = 0;

				this.records.forEach(record => {
					if (!record.tfoot)
						cnt++
				})

				return cnt
			},
			mutatedFields() {
				let breakpoint = this.mobileBreakpoint ? this.mobileBreakpoint : 1024

				if (window.innerWidth <= breakpoint) {
					return this.fields.filter(field => {
						if (("showOnMobile" in field) && !field.showOnMobile)
							return false
						else
							return true
					})
				}
				else if (window.innerWidth > breakpoint) {
					return this.fields.filter(field => {
						if (("showOnDesktop" in field) && !field.showOnDesktop)
							return false
						else
							return true
					})
				}
				else
					return this.fields
			},
			localRecordName() {
				if (this.recordName)
					return this.recordName
				else
					return "record"
			},
			computedEditButtonText() {
				if (this.editButtonText)
					return this.editButtonText
				else
					return "Edit"
			},
			computedEditButtonIcon() {
				if (this.editButtonText)
					return this.editButtonIcon
				else
					return "fa-pencil-alt"
			},
			mutatedRecords() {
				let vm = this

				let records = []
				if (this.records && Array.isArray(this.records)) {

					records = this.records.map(record => {
						let url = vm.hasEdit

						let id = 'id'
						if (vm.idField)
							id = vm.idField

						record.fieldId = record[id]

						if (!("rowClasses" in record))
							record.rowClasses = ""

						if (vm.selectedRecords.indexOf(record[id]) >= 0)
							record.selected = true
						else
							record.selected = false

						if (typeof url == "string") {

							if (url.search("{?}") >= 0)
								url = url.replace("{?}",record[id])
							else 
								url = url + record[id]

							record['editUrl'] = url
						}

						return record
					})
				}
				else
					records = this.records

				if (this.sortable && this.sorted) {

					// Previously selected column has a higher index than new, and fields were changed?
					let columnKey = ""

					if (vm.columnSortedIndex < vm.fields.length) 	// In range
						columnKey = vm.fields[vm.columnSortedIndex].key
					else {											// Out of range (use last column)
						columnKey = vm.fields[vm.fields.length - 1].key
						vm.columnSortedIndex = vm.fields.length - 1
					}

					records.sort((a,b) => {

						let comparerA, comparerB
						if ('sortBy' in vm.fields[vm.columnSortedIndex] && vm.fields[vm.columnSortedIndex].sortBy == 'float') {
							// Compare as if each cell has a string that represents a float or currency (eg. 13,432.77)
							// Use Case:  When limiting the length of a float (eg. to 2 decimal places) it is stored as a
							//            string.  However string float comparison is weird (eg. 2003...21....323 when sorted)
							// Use Case:  When sorting by currency as regular strings, number values and commas can
							//            cause inaccurate results (eg. 2,200...21.00...102,322,493)
							comparerA = parseFloat(a[columnKey].replaceAll(',', ''))
							comparerB = parseFloat(b[columnKey].replaceAll(',', ''))
						}
						else if ('sortBy' in vm.fields[vm.columnSortedIndex] && vm.fields[vm.columnSortedIndex].sortBy == 'htmlFloat') {
							// Get the inner text of an html string, convert it to a float
							// Use Case:  When table cells have special customization (eg. recoloured or icon included)
							//            and you want to sort by the value in the html
							// 			  eg. "<h2 class="fa fa-up">-99.5%</h2>" -> -99.5
							comparerA = parseFloat(new DOMParser().parseFromString(a[columnKey], "text/html").documentElement.textContent);
							comparerB = parseFloat(new DOMParser().parseFromString(b[columnKey], "text/html").documentElement.textContent);
						}
						else if ('sortBy' in vm.fields[vm.columnSortedIndex] && vm.fields[vm.columnSortedIndex].sortBy == 'date') {
							// Compare as if each cell represents a date (eg. July 4th, 1999)
							comparerA = this.moment(a[columnKey]).format("YYYY-MM-DD HH:mm:ss")
							comparerB = this.moment(b[columnKey]).format("YYYY-MM-DD HH:mm:ss")
						}
						else if (typeof a[columnKey] == "string" && typeof b[columnKey] == 'string') {
							comparerA = a[columnKey].toLowerCase()
							comparerB = b[columnKey].toLowerCase()
						}
						else if (typeof a[columnKey] == "object" && typeof b[columnKey] == "object") {
							//if tfoot, make sure its always sorted to bottom
							if ("tfoot" in a)
								return 1
							if ("tfoot" in b)
								return -1

							//if its an object, we're looking for the "text" property
							if (a[columnKey] && "text" in a[columnKey])
								comparerA = a[columnKey].text.toLowerCase()
							if (b[columnKey] && "text" in b[columnKey])
								comparerB = b[columnKey].text.toLowerCase()
						}
						else {
							//generic sort
							comparerA = a[columnKey]
							comparerB = b[columnKey]
						}

						if (vm.sortedAsc) {
							//sort ascending
							
							if (comparerA < comparerB)
								return -1
							else if (comparerA > comparerB)
								return 1
							else
								return 0
							
						}
						else {
							//sort descending

							if (comparerA < comparerB)
								return 1
							else if (comparerA > comparerB)
								return -1
							else
								return 0
							
						}
					})
				}

				return records
			},
			resolvedRecordName() {
				if (this.recordName)
					return this.recordName
				else
					return "Record"
			}
		},
		methods: {
			dragEnd(event) {
				this.$emit("dragEnd",event)
			},
			toggleRecordSelect(record) {
				let index = this.selectedRecords.indexOf(record.fieldId)

				if (index >= 0)
					this.selectedRecords.splice(index,1)
				else
					this.selectedRecords.push(record.fieldId)
			},
			headerClick(index) {
				//set column as sorted

				// Same column? Toggle sort
				if (index == this.columnSortedIndex)
					this.sortedAsc = !this.sortedAsc

				this.sorted = true
				this.columnSortedIndex = index

				this.$emit("headerClick",this.fields[index])
			},
			mutateButtons(record) {

				let vm = this

				if (this.otherButtons) {



					return this.otherButtons.filter(button => {
						if ("condition" in button) {
							// this button should show only conditionally on a matching record value
							if (record[button.condition[0]] == button.condition[1])
								return true
						}
						else return true
					})
					.map(button => {

						if ("link" in button) {
							let link = button.link

							if (typeof vm.idField == 'object') {
								vm.idField.forEach(placeholder => {
									let searchString = "{"+placeholder+"}"

									if (link.search(searchString) >= 0)
										link = link.replace(searchString,record[placeholder])
								})
							}
							else {
								if (link.search("{?}") >= 0)
									link = link.replace("{?}",record[vm.idField])
								else 
									link = link + record[vm.idField]
							}

							let mutatedButton = {
								icon: button.icon ? button.icon : null,
								link,
								class: button.class ? button.class : null
							}

							if ("text" in button)
								mutatedButton.text = button.text

							if ("title" in button)
								mutatedButton.title = button.title

							if ("newTab" in button)
								mutatedButton.newTab = button.newTab

							return mutatedButton
						}
						else if ("click" in button) {
							let mutatedButton = {
								icon: button.icon ? button.icon : null,
								click: button.click,
								class: button.class ? button.class : null
							}

							if ("text" in button)
								mutatedButton.text = button.text

							if ("title" in button)
								mutatedButton.title = button.title

							return mutatedButton
						}
					})
				}
				else
					return []
			},
			otherButtonClick(record,action,index) {
				this.$emit("otherButtonClick",record,action)
				this.$emit(action,record,index)
			},
			editRecord(record,index) {
				this.$emit("edit",record, index)
			},
			rowClick(record) {

				if (this.clickableRow) {
					if (this.hasEdit === true)
						this.$emit("edit",record)
					else
						this.$router.push(record.editUrl)
				}
			},
			deleteRecord(record, index) {

				if (this.simpleDelete)
					this.$emit("delete",record,index)
				
				else {
					this.confirmDialog.title = "Delete "+this.resolvedRecordName
					this.confirmDialog.message = "Are you sure you want to delete this " + this.resolvedRecordName + "?"
					this.confirmDialog.successFunction = "executeDelete"
					this.confirmDialog.buttonText = "Delete"
					this.confirmDialog.buttonClass = "btn-outline-danger"
					this.confirmDialog.parameters = {
						id: record.id
					}

					this.confirmDialog.visible = true
					this.confirmDialog.record = record
					this.confirmDialog.index = index
				}
			},
			executeDelete(parameters) {
				let vm = this

				//if delete endpoint is set, make request on that
				if (this.deleteEndpoint && 'id' in parameters && parameters.id) {
					let payload = {
						id: parameters.id
					}
					this.postData(this.deleteEndpoint,payload,true).then(function(response){
						if (response.success) {
							vm.$emit("modelChange")
							vm.$emit('delete',vm.confirmDialog.record, vm.confirmDialog.index)
							vm.confirmDialog.visible = false
							vm.alertSuccess(vm.recordName + " deleted.")
						}
					})
				}
				//otherwise simply $emit a delete event and pass the record
				else {
					this.$emit("delete", this.confirmDialog.record, this.confirmDialog.index)
					vm.confirmDialog.visible = false
				}
				
			}, 
			emitSelectedRecords() {
				let vm = this

 
				let recordSubset = this.records.filter(record => {
					let id = 'id'
					if (vm.idField)
						id = vm.idField

					if (vm.selectedRecords.indexOf(record[id]) >= 0)
						return true
					else 
						return false
				}).map(record => {
					let mutatedRecord = JSON.parse(JSON.stringify(record))

					delete mutatedRecord.fieldId

					return mutatedRecord
				})

				this.$emit("selectedRecordsChange",recordSubset)
			},
			selectDeselectAll() {
				let vm = this

				if (vm.bulkSelect) {
					vm.selectedRecords = vm.records.map(record => {
						if (vm.idField)
							return record[vm.idField]
						else
							return record.id
					})
				}
				else
					vm.selectedRecords = []

				this.emitSelectedRecords()
				
			}
		},
		created() {
			
		}
	}
</script>

<style scoped>

	tr .button-toggle-button {
		display: none;
	}

	th.sortable:hover {
		cursor: pointer;
		background-color: #e8e8e8;
	}

	.table-list .btn {
		border-radius: 5px;
		text-transform: uppercase;
		font-size: 9pt;
	}

	.table-list .btn-light {
		background-color: transparent;
		border: 1px solid var(--brand-primary-color);
	}

	.table-list .btn-outline-danger {
		background-color: transparent;
		color: #dc3545;
	}

	.table-list .btn-outline-danger:hover {
		background-color: #dc3545;
		
		color: #fff;
		font-size: 9pt;
	}

	.other-button, 
	.edit-button {
		margin-right: 4px;
	}

	.top-table-container {
		margin-bottom: 10px;
	}

	.record-select-label {
		margin: 0;
		text-align: center;
		padding: 5px;
		padding-top: 0;
		padding-bottom: 0;
	}

	.button-toggle-button {
		color: #424c50;
		font-size: 20px;
		padding-top: 0;
		padding-bottom: 0;
	}

	.button-toggle-button i {
		margin-top: 3px;
	}

	tr:hover .button-toggle-button {
		opacity: 0.8;
		cursor: pointer;
	}

	.table-row-buttons {
		margin-left: 10px;
	}

	.skeleton-cell {
		border-radius: 4px;
		background-color: #e0e0e0;
		width: 100%;
		height: 30px;
		animation: pulse 3s linear;
	}

	.btn-link .fa {
		font-size: 14pt;
	}

	.clickable-row:hover td {
		background-color: #EAE9E7;
		cursor: pointer;
	}

	@keyframes pulse {
		from {
			opacity: 1;
		}
		50% {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}
	
	/*@media (max-width: 1200px) {*/

		.table-row-buttons.hidden-buttons {
			display: none;
		}

		tr .button-toggle-button {
			display: block;
			text-align: right;
			width: 100%;
		}

		.table-row-buttons .btn {
			display: block;
			width: calc(100% - 4px);
			border-radius: 0;
			border-bottom: none;
		}

		.table-row-buttons .btn:first-child {
			border-top-left-radius: 3px;
			border-top-right-radius: 3px;
		}

		.table-row-buttons .btn:last-child {
			border-bottom-left-radius: 3px;
			border-bottom-right-radius: 3px;
			border: 1px solid var(--brand-primary-color); /*#88867F;*/
		}
	/*}*/

</style>